import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Cake,
  Wc,
  CalendarMonth,
  Home,
  Translate,
  Church,
  Groups,
  AccessTime,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { formatChildAge, stringAvatar } from "utils";
import { IChild, IChildDietary } from "types";
import { setChildDietary } from "store/features/children";
import ChildDietaryCard from "components/ChildDietaryCard";

interface TabPanelProps {
  index: number;
  value: number;
  item: IChild;
}

interface DetailRowProps {
  icon: React.ReactElement;
  label: string;
  value: string;
}

const DetailRow: React.FC<DetailRowProps> = ({ icon, label, value }) => {
  const theme = useTheme();
  return (
    <Box display="flex" alignItems="flex-start" gap={1.5} py={1.5}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          color: "primary.main",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={600}
          sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
        >
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={500}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

const Child: React.FC<TabPanelProps> = ({ value, index, item }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();

  const ageText = useMemo(
    () => formatChildAge(item.dateOfBirth, t) ?? "",
    [item.dateOfBirth, t],
  );

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`child-tabpanel-${index}`}
      aria-labelledby={`child-tab-${index}`}
    >
      {value === index && (
        <Container sx={{ py: 3, maxWidth: 600 }} maxWidth={false}>
          {/* Profile header */}
          <Card
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              mb: 3,
            }}
            elevation={0}
            variant="outlined"
          >
            {/* Gradient banner */}
            <Box
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                height: 80,
                position: "relative",
              }}
            />

            <CardContent sx={{ pt: 0, pb: 3, textAlign: "center" }}>
              {/* Avatar overlapping banner */}
              <Avatar
                src={item.photo as string | undefined}
                {...(!item.photo
                  ? stringAvatar(`${item.firstName} ${item.lastName}`, {
                      width: 88,
                      height: 88,
                      fontSize: 32,
                      border: `4px solid ${theme.palette.background.paper}`,
                      mx: "auto",
                      mt: "-44px",
                      mb: 1.5,
                      boxShadow: 2,
                    })
                  : {
                      sx: {
                        width: 88,
                        height: 88,
                        fontSize: 32,
                        border: `4px solid ${theme.palette.background.paper}`,
                        mx: "auto",
                        mt: "-44px",
                        mb: 1.5,
                        boxShadow: 2,
                      },
                    })}
              />

              <Typography variant="h6" fontWeight={700}>
                {item.firstName} {item.lastName}
              </Typography>

              <Box display="flex" justifyContent="center" gap={1} mt={1}>
                <Chip
                  size="small"
                  label={
                    item.gender === "Male"
                      ? t("gender.male")
                      : t("gender.female")
                  }
                  icon={<Wc sx={{ fontSize: 14 }} />}
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label={ageText}
                  icon={<Cake sx={{ fontSize: 14 }} />}
                  variant="outlined"
                />
                {item.classroom?.name && (
                  <Chip
                    size="small"
                    label={item.classroom.name}
                    icon={<Groups sx={{ fontSize: 14 }} />}
                    variant="outlined"
                  />
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Details */}
          <Card
            sx={{ borderRadius: 3, mb: 3 }}
            elevation={0}
            variant="outlined"
          >
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                {t("labels.general_info")}
              </Typography>

              <DetailRow
                icon={<CalendarMonth sx={{ fontSize: 18 }} />}
                label={t("inputs.birthday")}
                value={dayjs(item.dateOfBirth).format("DD MMM YYYY")}
              />
              <DetailRow
                icon={<Home sx={{ fontSize: 18 }} />}
                label={t("address")}
                value={item.address || t("notSpecified")}
              />
              <DetailRow
                icon={<AccessTime sx={{ fontSize: 18 }} />}
                label={t("labels.attendance")}
                value={
                  item.attendanceMode
                    ? t("labels.part_time")
                    : t("labels.full_time")
                }
              />
            </CardContent>
          </Card>

          {/* Additional info */}
          <Card sx={{ borderRadius: 3 }} elevation={0} variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                {t("additionalInfo")}
              </Typography>

              <DetailRow
                icon={<Groups sx={{ fontSize: 18 }} />}
                label={t("ethnicity")}
                value={item.additionals?.ethnicity || t("notSpecified")}
              />
              <DetailRow
                icon={<Church sx={{ fontSize: 18 }} />}
                label={t("religion")}
                value={item.additionals?.religion || t("notSpecified")}
              />
              <DetailRow
                icon={<Translate sx={{ fontSize: 18 }} />}
                label={t("languages")}
                value={
                  item.additionals?.languages?.join(", ") || t("notSpecified")
                }
              />
            </CardContent>
          </Card>

          {/* Dietary & allergies — parents add/maintain this */}
          <Box mt={3}>
            <ChildDietaryCard
              childId={item.id as string}
              dietary={item.dietary}
              onChanged={(d: IChildDietary) =>
                dispatch(
                  setChildDietary({ childId: Number(item.id), dietary: d }),
                )
              }
            />
          </Box>
        </Container>
      )}
    </div>
  );
};

export default Child;
